// TODO fix typo at the fucking folder name SaveToShortlistForm => SaveToShortListForm

import React, { useImperativeHandle, useState, useEffect, forwardRef } from "react";
import Image from "next/image";
import { useFormik } from "formik";
import { Divider, Spin } from "antd";
import { GoSearch } from "react-icons/go";
import { FaChevronLeft } from "react-icons/fa";

//components
import Input from "@/shared/Input/Input";
import Button from "@/shared/Button";
import Select from "@/shared/Select";
import ShortlistCampaignCard from "../ShortlistCampaignCard";
import notification from "@/components/notifications";

//helpers
import campaigns from "@/api/query/campaigns";
import purposes from "@/api/query/purposes";
import { validationSchemaCreateCampaign } from "@/helpers/schemas/validationSchemaCreateCampaign";
import { addSpaceToCampaign } from "@/api/campaigns";
import useUserProfile from "@/hooks/useUserProfile";
import customDataLayerPush from "@/helpers/customDataLayerPush";
import { useDimension } from "@/hooks/useDimensions";
import { handleCreateActivity } from "@/helpers/handleCreateActivity";
import { useCompletedStepModalContext } from "@/contexts/CompletedStepModalContext";
import dayjs from "@/helpers/dayjs";

// interfaces
import { ISaveToShortlistForm, ISaveToShortlistFormValues } from "./ISaveToShortListForm";
import { IAxiosResponseError } from "@/types/api/AxiosResponseError";
import { ISpaceFullResponse } from "@/types/Space";
import { ICampaign, ICampaignItem } from "@/types/api/Campaign";

// assets
import UndrawEventsImg from "@/assets/images/undraw_events.svg";
import styles from "@/assets/styles/containers/save-to-shortlist-form.module.scss";


export const getSuccessMessage = (spaceName: string, campaign: ICampaign) => (<>
  Congratulations! Space {spaceName} was added to the campaign <a href={`/campaign/${campaign.id}/planner`}>{ campaign.name }</a>
</>);

export type Ref = React.Ref<{
  submit: (e?: React.FormEvent<HTMLFormElement> | undefined) => void;
}>;

const SaveToShortlistForm = (props: ISaveToShortlistForm, ref: Ref) => {
  const {
    spaceData = {} as ISpaceFullResponse,
    period,
    onSuccess,
    onCancel,
    onError,
    allCampaigns,
    isCampaignLoading,
    campaignRefetch,
    onCardClick,
  } = props;

  const [searchInput, setSearchInput] = useState("");
  const [formLoading, setFormLoading] = useState(false);
  const [isCreateCampaign, setIsCreateCampaign] = useState(false);

  const { data: purposeList = [] } = purposes.usePurposesList();
  const { mutateAsync: quickCreate } = campaigns.useQuickCreate();

  const { setState: completedStepModalSetState } = useCompletedStepModalContext();

  const { data: userData } = useUserProfile();
  const [width] = useDimension();

  const isMobile = width <= 720;

  const campaignsToRender = allCampaigns?.results?.filter(c => c.state !== "Archived") || [];

  const searchedCampaigns = searchInput
    ? campaignsToRender.filter(el => el.name.toLowerCase().includes(searchInput.toLowerCase()))
    : campaignsToRender;
  const purposesOptions = purposeList.map(purpose => ({ label: purpose.name, value: purpose.id }));


  const INITIAL_VALUES = {
    name: "",
    purpose: purposesOptions[0]
  };

  const { values, setFieldValue, errors, handleSubmit, setErrors, resetForm } = useFormik<ISaveToShortlistFormValues>({
    initialValues: INITIAL_VALUES,
    validationSchema: validationSchemaCreateCampaign,
    validateOnBlur: false,
    validateOnChange: true,
    validateOnMount: false,
    enableReinitialize: true,
    onSubmit: onSubmit
  });

  async function onSubmit(data: ISaveToShortlistFormValues) {
    const payload = { ...data, purpose: Number(data.purpose.value) };

    try {
      setFormLoading(true);
      const res = await quickCreate(payload);

      customDataLayerPush("create_campaign", userData?.id as number);

      if (spaceData && spaceData.id) {
        const { data: activityData } = await addSpaceToCampaign(res.data.id, spaceData.id);

        if (userData?.progressBar as number < 4) {
          completedStepModalSetState({
            open: true,
            type: "create-campaign-add-space",
            spaceSlug: spaceData.slug,
            locationSlug: spaceData.location.slug,
            progressBar: 4
          });
          
          onCancel && onCancel();
        }

        if (period && period.start && period.end) {
          await handleCreateActivity(res.data.id, activityData.id, period);
        }

        notification({
          placement: "topRight",
          duration: 30,
          actionText: "Go to campaign",
          actionProps: { href: `/campaign/${res.data.id}/planner` },
          message: getSuccessMessage(spaceData.name, res.data),
          type: "success",
        });

        onSuccess && onSuccess(true);
        setFormLoading(false);
      }

      setIsCreateCampaign(false);
    } catch (ex: unknown) {
      const error = ex as IAxiosResponseError;

      onError && onError(error);
      setErrors(error.response.data);
      setFormLoading(false);
    } finally {
      campaignRefetch && campaignRefetch();
    }
  }

  useImperativeHandle(ref, () => ({ submit: handleSubmit }));

  useEffect(() => {
    resetForm();
  }, [isCreateCampaign]);

  const handleCampaignToggle = async (e: boolean, campaign: ICampaign, activityData: ICampaignItem) => {
    if (!e) {
      await handleCreateActivity(campaign.id, activityData.id, period);

      if (userData?.progressBar as number < 4) {
        completedStepModalSetState({
          open: true,
          type: "add-space",
          spaceSlug: spaceData.slug,
          locationSlug: spaceData.location.slug,
          progressBar: 4
        });

        onCancel && onCancel();
      }

      notification({
        placement: "topRight",
        duration: 30,
        actionText: "Go to campaign",
        actionProps: { href: `/campaign/${campaign.id}/planner` },
        message: getSuccessMessage(spaceData.name, campaign),
        type: "success",
      });
    }
    campaignRefetch && campaignRefetch();
  };

  const handleCreateCampaignClose = () => {
    setIsCreateCampaign(false);
  };

  // renders
  const renderExistingCampaignsTitle = () => <p className={styles["locl-save-to-shortlist-form__head-text"]}>Add to favourites</p>;

  const renderCreateCampaignsTitle = () => (
    <div className={styles["locl-save-to-shortlist-form__head-row"]}>
      {
        !!campaignsToRender.length && (
          <div
            className={styles["locl-save-to-shortlist-form__head-icon"]}
            onClick={() => setIsCreateCampaign(false)}
            data-testid="locl-save-to-shortlist-create-button-back"
          >
            <FaChevronLeft />
          </div>
        )
      }
      <p className={styles["locl-save-to-shortlist-form__head-text"]}>
        Create new campaign
      </p>
    </div>
  );

  const renderExistingCampaigns = () => {
    if (campaignsToRender.length > 0) {
      return (
        <>
          <Input
            onChange={(e) => setSearchInput(e.target.value)}
            placeholder="Search for campaigns"
            wrapperClassName={styles["locl-save-to-shortlist-form__search"]}
            addonBefore={<GoSearch />}
            data-testid="locl-save-to-shortlist-form-input-search"
          />
          <div className={styles["locl-save-to-shortlist-form__campaigns"]}>
            <p className={styles["locl-save-to-shortlist-form__campaigns_title"]}>
              Select from existing campaigns
            </p>
            <div className={styles["locl-save-to-shortlist-form__campaigns_wrapper"]}>
              {searchedCampaigns.map((campaign, index) => (
                <ShortlistCampaignCard
                  key={`${campaign.id}-${index}`}
                  campaign={campaign}
                  space={spaceData}
                  onClick={() => onCardClick && onCardClick(campaign)}
                  onCampaignToggle={onCardClick
                    ? null
                    : (e, activityData) => handleCampaignToggle(e, campaign, activityData as ICampaignItem)
                  }
                  isMobile={isMobile}
                />
              ))}
            </div>
          </div>
          <div className={styles["locl-save-to-shortlist-form__campaigns__footer"]}>
            <Divider className="locl-save-to-shortlist-form__campaigns__footer-line">
              <p className={styles["locl-save-to-shortlist-form__campaigns__footer-title"]}>
                Are you working on something else?
              </p>
            </Divider>
            <div className={styles["locl-save-to-shortlist-form__campaigns__footer-btn"]}>
              <Button
                onClick={() => setIsCreateCampaign(true)}
                type="primary"
                data-testid="locl-save-to-shortlist-form-button-create"
              >
                Create a new campaign
              </Button>
            </div>
          </div>
        </>
      );
    }

    return (<>
      <div className={styles["locl-save-to-shortlist-form__empty"]} data-testid="locl-save-to-shortlist-form-empty">
        <Image
          src={UndrawEventsImg}
          width={230}
          height={176}
          className={styles["locl-save-to-shortlist-form__empty__img"]}
          alt="undraw events img"
        />
        <div className={styles["locl-save-to-shortlist-form__empty__info"]}>
          <div className={styles["locl-save-to-shortlist-form__empty__title"]}>
            You don’t have campaigns yet
          </div>
          <div className={styles["locl-save-to-shortlist-form__empty__subtitle"]}>
            Let’s create your first campaign together!
          </div>
        </div>
        
      </div>
      <div className={styles["locl-save-to-shortlist-form__campaigns__footer"]}>
        <div className={styles["locl-save-to-shortlist-form__campaigns__footer-btn"]}>
          <Button
            onClick={() => setIsCreateCampaign(true)}
            type="primary"
            data-testid="locl-save-to-shortlist-form-button-create"
          >
            Create a new campaign
          </Button>
        </div>
      </div>
    </>);
  };

  const renderCreateCampaigns = () => (
    <div className={styles["locl-save-to-shortlist-form__create-campaign"]}>
      <div className={styles["locl-save-to-shortlist-form__create-campaign_form"]}>
        <div className={styles["locl-save-to-shortlist-form__create-campaign_form_element"]}>
          <p className={styles["locl-save-to-shortlist-form__create-campaign_form_element-label"]}>Campaign Name</p>
          <Input
            error={errors.name}
            placeholder="Campaign name"
            className="locl-save-to-shortlist-form__create-campaign_form_element_input"
            value={values.name}
            onChange={(e) => setFieldValue("name", e.target.value)}
            data-testid="locl-save-to-shortlist-form-create-input-name"
          />
        </div>
        <div className={styles["locl-save-to-shortlist-form__create-campaign_form_element"]}>
          <p className={styles["locl-save-to-shortlist-form__create-campaign_form_element-label"]}>Use of Space</p>
          <Select
            fullWidth={true}
            options={purposesOptions}
            value={values.purpose}
            onChange={(e) => setFieldValue("purpose", e)}
            aria-label="locl-save-to-shortlist-form-create-select-purpose"
          />
        </div>
      </div>
      <div className={styles["locl-save-to-shortlist-form__create-campaign__footer"]}>
        <Button
          type="secondary"
          key="cancel"
          onClick={handleCreateCampaignClose}
          data-testid="locl-save-to-shortlist-form-create-button-cancel"
        >
          Cancel
        </Button>
        <Button
          onClick={handleSubmit as () => void}
          loading={formLoading}
          type="primary"
          key="submit"
          data-testid="locl-save-to-shortlist-form-create-button-submit"
        >
          Save and continue
        </Button>
      </div>
    </div>
  );


  const renderContent = () => {
    if (isCreateCampaign) {
      return renderCreateCampaigns();
    }
  
    return renderExistingCampaigns();
  };

  if (isCampaignLoading) {
    return (
      <div className={styles["locl-save-to-shortlist-form__loader"]}>
        <Spin className={styles["locl-save-to-shortlist-form__loader__spin"]} />
      </div>
    )
  }
  
  return (
    <div className={styles["locl-save-to-shortlist-form__wrapper"]} data-testid="locl-save-to-shortlist-form-wrapper">
      <div className={styles["locl-save-to-shortlist-form__head"]}>
        { isCreateCampaign
          ? renderCreateCampaignsTitle()
          : renderExistingCampaignsTitle()
        }
      </div>
      {period && period.start && period.end && (
        <div className={styles["locl-save-to-shortlist-form__selected-period"]}>
          <span>{`${spaceData.name} 
        ${dayjs(new Date(period.start)).format("DD MMM YYYY")} - 
        ${dayjs(new Date(period.end)).format("DD MMM YYYY")}`}
          </span>
        </div>
      )}
      { renderContent() }
    </div>
  );
};

SaveToShortlistForm.displayName = "SaveToShortlistForm";


export default forwardRef(SaveToShortlistForm);
